import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { MobileNav } from "@/components/MobileNav";
import { toast } from "sonner";

const ACCENTS = {
  gold: { text: "text-[#b45309]", bg: "bg-[#f59e0b]" },
  silver: { text: "text-[#0284c7]", bg: "bg-[#38bdf8]" },
};

function LoginForm({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onSuccess(data);
    } catch (err) {
      setError(formatApiError(err, "Accesso non riuscito."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white border border-[#e2e8f0] rounded-2xl p-8 md:p-10">
      <span className="font-mono-x text-xs text-[#0284c7] uppercase tracking-widest block mb-1">
        Area Riservata
      </span>
      <h1 className="font-display text-2xl text-[#0f172a] mb-6">Login Admin</h1>
      <form onSubmit={submit} className="space-y-3" data-testid="admin-login-form">
        <input
          type="email"
          required
          data-testid="admin-email-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-3 py-2 bg-[#e3edf6] border border-[#cbd5e1] rounded text-xs text-[#0f172a] placeholder-[#64748b] focus:border-[#38bdf8] focus:outline-none"
          placeholder="Email"
        />
        <input
          type="password"
          required
          data-testid="admin-password-input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full px-3 py-2 bg-[#e3edf6] border border-[#cbd5e1] rounded text-xs text-[#0f172a] placeholder-[#64748b] focus:border-[#38bdf8] focus:outline-none"
          placeholder="Password"
        />
        {error && (
          <p data-testid="admin-login-error" className="text-xs text-red-600 font-mono-x">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="w-full py-2.5 bg-[#38bdf8] text-[#0f172a] font-bold text-xs uppercase tracking-wider rounded hover:bg-[#7dd3fc] transition-colors disabled:opacity-50"
        >
          {loading ? "Accesso..." : "Accedi"}
        </button>
      </form>
    </div>
  );
}

function ResultEditor({ match, accent, onSaved }) {
  const [hs, setHs] = useState(match.played ? String(match.home_sets) : "");
  const [as, setAs] = useState(match.played ? String(match.away_sets) : "");
  const [saving, setSaving] = useState(false);
  const [showPostpone, setShowPostpone] = useState(false);
  const [recDate, setRecDate] = useState("");

  useEffect(() => {
    setHs(match.played ? String(match.home_sets) : "");
    setAs(match.played ? String(match.away_sets) : "");
  }, [match.id, match.played, match.home_sets, match.away_sets]);

  const save = async () => {
    if (hs === "" || as === "") {
      toast.error("Inserisci entrambi i punteggi");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/matches/${match.id}/result`, { home_sets: Number(hs), away_sets: Number(as) });
      toast.success("Risultato salvato");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err, "Salvataggio non riuscito"));
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    try {
      await api.delete(`/matches/${match.id}/result`);
      toast.success("Risultato rimosso");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err, "Operazione non riuscita"));
    } finally {
      setSaving(false);
    }
  };

  const togglePostponed = async () => {
    setSaving(true);
    try {
      await api.put(`/matches/${match.id}/postponed`, {
        postponed: !match.postponed,
        recovery_date: !match.postponed && recDate ? recDate : null,
      });
      toast.success(match.postponed ? "Rinvio rimosso" : "Gara segnalata come rinviata");
      setShowPostpone(false);
      setRecDate("");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err, "Operazione non riuscita"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid={`admin-match-${match.id}`}
      className="p-4 bg-[#e3edf6] rounded border border-[#e2e8f0] flex flex-col gap-3"
    >
      <div className="flex justify-between items-center text-xs">
        <span className={`font-ui ${match.date_label.startsWith("DOM") ? (accent === "gold" ? "text-[#0891b2]" : "text-[#dc2626]") : ACCENTS[accent].text}`}>
          {match.date_label} • {match.time}
        </span>
        <span className="flex items-center gap-2">
          {match.played && <span className="font-ui text-[10px] text-emerald-600 uppercase">Refertato</span>}
          {match.postponed && (
            <span data-testid={`postponed-badge-${match.id}`} className="font-ui text-[10px] text-[#b45309] uppercase bg-[#fef3c7] border border-[#fcd34d] rounded px-2 py-0.5">
              Rinviata
            </span>
          )}
        </span>
      </div>
      <div className="text-sm text-[#0f172a] font-medium">
        {match.home} <span className="text-[#64748b] font-mono-x text-xs">vs</span> {match.away}
      </div>
      {match.postponed ? (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#b45309] font-ui">
              Gara rinviata
              {match.recovery_date
                ? ` — recupero: ${new Date(match.recovery_date + "T00:00:00").toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short" })}`
                : " — da recuperare"}
            </span>
            <button
              onClick={togglePostponed}
              disabled={saving}
              data-testid={`restore-match-${match.id}`}
              className="ml-auto px-3 py-1.5 border border-[#cbd5e1] text-[#475569] text-xs rounded hover:text-[#0f172a] hover:border-[#64748b] transition-colors disabled:opacity-50"
            >
              Ripristina gara
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
        <input
          type="number"
          min="0"
          max="4"
          data-testid={`score-home-${match.id}`}
          value={hs}
          onChange={(e) => setHs(e.target.value)}
          className="w-16 px-2 py-1.5 bg-white border border-[#cbd5e1] rounded text-center text-sm text-[#0f172a] focus:border-[#38bdf8] focus:outline-none"
          placeholder="0"
        />
        <span className="text-[#64748b] font-mono-x text-xs">-</span>
        <input
          type="number"
          min="0"
          max="4"
          data-testid={`score-away-${match.id}`}
          value={as}
          onChange={(e) => setAs(e.target.value)}
          className="w-16 px-2 py-1.5 bg-white border border-[#cbd5e1] rounded text-center text-sm text-[#0f172a] focus:border-[#38bdf8] focus:outline-none"
          placeholder="0"
        />
        <button
          onClick={save}
          disabled={saving}
          data-testid={`save-result-${match.id}`}
          className="ml-auto px-4 py-1.5 bg-[#38bdf8] text-[#0f172a] font-bold text-xs uppercase tracking-wider rounded hover:bg-[#7dd3fc] transition-colors disabled:opacity-50"
        >
          Salva
        </button>
        {match.played && (
          <button
            onClick={clear}
            disabled={saving}
            data-testid={`clear-result-${match.id}`}
            className="px-3 py-1.5 border border-[#cbd5e1] text-[#475569] text-xs rounded hover:text-[#0f172a] hover:border-[#64748b] transition-colors disabled:opacity-50"
          >
            Annulla
          </button>
        )}
        {!match.played && (
          <button
            onClick={() => setShowPostpone((v) => !v)}
            disabled={saving}
            data-testid={`postpone-match-${match.id}`}
            className="px-3 py-1.5 border border-[#fcd34d] bg-[#fef3c7] text-[#b45309] text-xs rounded hover:bg-[#fde68a] transition-colors disabled:opacity-50"
          >
            Rinviata
          </button>
        )}
        </div>
        {showPostpone && !match.played && (
          <div className="flex items-center gap-2" data-testid={`postpone-form-${match.id}`}>
            <label className="text-[11px] font-ui text-[#475569]">Data di recupero (facoltativa):</label>
            <input
              type="date"
              data-testid={`recovery-date-${match.id}`}
              value={recDate}
              onChange={(e) => setRecDate(e.target.value)}
              className="px-2 py-1.5 bg-white border border-[#cbd5e1] rounded text-xs text-[#0f172a] focus:border-[#38bdf8] focus:outline-none"
            />
            <button
              onClick={togglePostponed}
              disabled={saving}
              data-testid={`confirm-postpone-${match.id}`}
              className="px-3 py-1.5 bg-[#f59e0b] text-[#0f172a] font-bold text-xs uppercase tracking-wider rounded hover:bg-[#fbbf24] transition-colors disabled:opacity-50"
            >
              Conferma rinvio
            </button>
          </div>
        )}
        </div>
      )}
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const [leagues, setLeagues] = useState(null);
  const [league, setLeague] = useState("gold");
  const [gironeIdx, setGironeIdx] = useState(0);
  const [giornata, setGiornata] = useState(1);
  const [tab, setTab] = useState("risultati");
  const [contacts, setContacts] = useState([]);
  const [press, setPress] = useState([]);
  const [pressForm, setPressForm] = useState({ outlet: "", title: "", url: "", date: "" });
  const [pressSaving, setPressSaving] = useState(false);

  const loadLeagues = () =>
    api
      .get("/leagues")
      .then((res) => setLeagues(res.data))
      .catch(() => toast.error("Impossibile caricare i calendari"));

  const loadContacts = () =>
    api
      .get("/contacts")
      .then((res) => setContacts(res.data))
      .catch(() => toast.error("Impossibile caricare i messaggi"));

  const loadPress = () =>
    api
      .get("/press")
      .then((res) => setPress(res.data))
      .catch(() => toast.error("Impossibile caricare gli articoli"));

  useEffect(() => {
    loadLeagues();
    loadContacts();
    loadPress();
  }, []);

  const addArticle = async (e) => {
    e.preventDefault();
    setPressSaving(true);
    try {
      await api.post("/press", pressForm);
      toast.success("Articolo aggiunto");
      setPressForm({ outlet: "", title: "", url: "", date: "" });
      loadPress();
    } catch (err) {
      toast.error(formatApiError(err, "Salvataggio non riuscito"));
    } finally {
      setPressSaving(false);
    }
  };

  const removeArticle = async (id) => {
    try {
      await api.delete(`/press/${id}`);
      toast.success("Articolo rimosso");
      loadPress();
    } catch (err) {
      toast.error(formatApiError(err, "Operazione non riuscita"));
    }
  };

  const gironi = leagues ? leagues[league].gironi : [];
  const girone = gironi[Math.min(gironeIdx, gironi.length - 1)];
  const round = girone?.rounds.find((r) => r.n === giornata) || girone?.rounds[0];

  const stats = useMemo(() => {
    if (!leagues) return { total: 0, played: 0 };
    let total = 0;
    let played = 0;
    ["gold", "silver"].forEach((l) =>
      leagues[l].gironi.forEach((g) =>
        g.rounds.forEach((r) =>
          r.matches.forEach((m) => {
            total += 1;
            if (m.played) played += 1;
          })
        )
      )
    );
    return { total, played };
  }, [leagues]);

  return (
    <div className="max-w-6xl mx-auto" data-testid="admin-dashboard">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <span className="font-mono-x text-xs text-[#0284c7] uppercase tracking-widest block mb-1">
            Pannello Gestione
          </span>
          <h1 className="font-display text-3xl text-[#0f172a]">Inserimento Risultati</h1>
          <p className="text-xs font-ui text-[#64748b] mt-1">
            {user.email} • {stats.played}/{stats.total} partite refertate
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="tab-risultati"
            onClick={() => setTab("risultati")}
            className={`px-4 py-2 rounded text-xs font-ui uppercase tracking-wider border transition-all ${
              tab === "risultati"
                ? "bg-[#0f172a] text-white border-[#0f172a] font-bold"
                : "bg-[#e2e8f0] text-[#475569] border-[#cbd5e1] hover:text-[#0f172a]"
            }`}
          >
            Risultati
          </button>
          <button
            data-testid="tab-messaggi"
            onClick={() => setTab("messaggi")}
            className={`px-4 py-2 rounded text-xs font-ui uppercase tracking-wider border transition-all ${
              tab === "messaggi"
                ? "bg-[#0f172a] text-white border-[#0f172a] font-bold"
                : "bg-[#e2e8f0] text-[#475569] border-[#cbd5e1] hover:text-[#0f172a]"
            }`}
          >
            Messaggi ({contacts.length})
          </button>
          <button
            data-testid="tab-stampa"
            onClick={() => setTab("stampa")}
            className={`px-4 py-2 rounded text-xs font-ui uppercase tracking-wider border transition-all ${
              tab === "stampa"
                ? "bg-[#0f172a] text-white border-[#0f172a] font-bold"
                : "bg-[#e2e8f0] text-[#475569] border-[#cbd5e1] hover:text-[#0f172a]"
            }`}
          >
            Stampa ({press.length})
          </button>
          <button
            data-testid="admin-logout-button"
            onClick={onLogout}
            className="px-4 py-2 border border-[#cbd5e1] text-[#475569] text-xs font-ui uppercase tracking-wider rounded hover:text-[#0f172a] hover:border-[#64748b] transition-colors"
          >
            Esci
          </button>
        </div>
      </div>

      {tab === "risultati" && (
        <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 md:p-6">
          <div className="flex gap-3 mb-6">
            <button
              data-testid="admin-league-gold"
              onClick={() => {
                setLeague("gold");
                setGironeIdx(0);
                setGiornata(1);
              }}
              className={`px-4 py-2 rounded border transition-all flex items-center bg-white ${
                league === "gold"
                  ? "border-[#f59e0b] ring-2 ring-[#f59e0b]/40"
                  : "border-[#cbd5e1] hover:border-[#f59e0b]"
              }`}
            >
              <img src="/logos/girone-gold.png" alt="Girone Gold" className="h-6 w-auto" />
            </button>
            <button
              data-testid="admin-league-silver"
              onClick={() => {
                setLeague("silver");
                setGironeIdx(0);
                setGiornata(1);
              }}
              className={`px-4 py-2 rounded border transition-all flex items-center bg-white ${
                league === "silver"
                  ? "border-[#94a3b8] ring-2 ring-[#94a3b8]/40"
                  : "border-[#cbd5e1] hover:border-[#94a3b8]"
              }`}
            >
              <img src="/logos/girone-silver.png" alt="Girone Silver" className="h-6 w-auto" />
            </button>
          </div>

          {!leagues && <p className="text-[#475569] font-mono-x text-xs py-8 text-center">Caricamento...</p>}

          {leagues && girone && (
            <>
              <div className="flex flex-wrap gap-2 mb-4">
                {gironi.map((g, i) => (
                  <button
                    key={g.name}
                    data-testid={`admin-girone-${g.name}`}
                    onClick={() => {
                      setGironeIdx(i);
                      setGiornata(1);
                    }}
                    className={`px-4 py-1.5 rounded text-xs font-ui border transition-all ${
                      i === gironeIdx
                        ? `${ACCENTS[league].bg} text-[#0f172a] border-transparent font-bold`
                        : "bg-[#e3edf6] text-[#475569] border-[#cbd5e1] hover:text-[#0f172a]"
                    }`}
                  >
                    Girone {g.name}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 mb-6">
                {girone.rounds.map((r) => (
                  <button
                    key={r.n}
                    data-testid={`admin-giornata-${r.n}`}
                    onClick={() => setGiornata(r.n)}
                    className={`px-3 py-1.5 rounded text-[11px] font-ui border transition-all ${
                      r.n === giornata
                        ? "bg-[#0f172a] text-white border-[#0f172a] font-bold"
                        : "bg-[#e3edf6] text-[#475569] border-[#cbd5e1] hover:text-[#0f172a]"
                    }`}
                  >
                    {r.n}° • {r.label}
                  </button>
                ))}
              </div>
              {round && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {round.matches.map((m) => (
                    <ResultEditor key={m.id} match={m} accent={league} onSaved={loadLeagues} />
                  ))}
                </div>
              )}
              {round?.bye && (
                <p className="mt-4 text-xs font-ui text-[#64748b] text-center border border-dashed border-[#cbd5e1] rounded p-3">
                  Riposa: {round.bye}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {tab === "stampa" && (
        <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 md:p-6" data-testid="press-panel">
          <h2 className="font-bold text-lg text-[#0f172a] mb-4">Rassegna stampa</h2>
          <form onSubmit={addArticle} className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-testid="press-add-form">
            <input
              required
              data-testid="press-outlet-input"
              value={pressForm.outlet}
              onChange={(e) => setPressForm({ ...pressForm, outlet: e.target.value })}
              className="px-3 py-2 bg-[#e3edf6] border border-[#cbd5e1] rounded text-xs text-[#0f172a] placeholder-[#64748b] focus:border-[#38bdf8] focus:outline-none"
              placeholder="Testata (es. PerugiaToday)"
            />
            <input
              required
              type="date"
              data-testid="press-date-input"
              value={pressForm.date}
              onChange={(e) => setPressForm({ ...pressForm, date: e.target.value })}
              className="px-3 py-2 bg-[#e3edf6] border border-[#cbd5e1] rounded text-xs text-[#0f172a] focus:border-[#38bdf8] focus:outline-none"
            />
            <input
              required
              data-testid="press-title-input"
              value={pressForm.title}
              onChange={(e) => setPressForm({ ...pressForm, title: e.target.value })}
              className="px-3 py-2 bg-[#e3edf6] border border-[#cbd5e1] rounded text-xs text-[#0f172a] placeholder-[#64748b] focus:border-[#38bdf8] focus:outline-none md:col-span-2"
              placeholder="Titolo dell'articolo"
            />
            <input
              required
              type="url"
              data-testid="press-url-input"
              value={pressForm.url}
              onChange={(e) => setPressForm({ ...pressForm, url: e.target.value })}
              className="px-3 py-2 bg-[#e3edf6] border border-[#cbd5e1] rounded text-xs text-[#0f172a] placeholder-[#64748b] focus:border-[#38bdf8] focus:outline-none md:col-span-2"
              placeholder="Link all'articolo (https://...)"
            />
            <button
              type="submit"
              disabled={pressSaving}
              data-testid="press-add-submit"
              className="md:col-span-2 py-2.5 bg-[#38bdf8] text-[#0f172a] font-bold text-xs uppercase tracking-wider rounded hover:bg-[#7dd3fc] transition-colors disabled:opacity-50"
            >
              {pressSaving ? "Salvataggio..." : "Aggiungi articolo"}
            </button>
          </form>
          {press.length === 0 && (
            <p className="text-[#475569] font-mono-x text-xs py-8 text-center">Nessun articolo inserito.</p>
          )}
          <div className="space-y-3">
            {press.map((a) => (
              <div key={a.id} data-testid={`press-item-${a.id}`} className="p-4 bg-[#e3edf6] rounded border border-[#e2e8f0] flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1 text-xs">
                    <span className="text-[#0284c7] font-bold font-ui uppercase">{a.outlet}</span>
                    <span className="font-mono-x text-[#64748b]">
                      {new Date(a.date).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" })}
                    </span>
                  </div>
                  <a href={a.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0f172a] font-medium hover:text-[#0284c7] transition-colors">
                    {a.title}
                  </a>
                </div>
                <button
                  onClick={() => removeArticle(a.id)}
                  data-testid={`press-delete-${a.id}`}
                  className="shrink-0 px-3 py-1.5 border border-[#cbd5e1] text-[#475569] text-xs rounded hover:text-red-600 hover:border-red-300 transition-colors"
                >
                  Elimina
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "messaggi" && (
        <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 md:p-6" data-testid="contacts-list">
          <h2 className="font-bold text-lg text-[#0f172a] mb-4">Messaggi ricevuti</h2>
          {contacts.length === 0 && (
            <p className="text-[#475569] font-mono-x text-xs py-8 text-center">Nessun messaggio ricevuto.</p>
          )}
          <div className="space-y-3">
            {contacts.map((c, i) => (
              <div key={i} data-testid={`contact-message-${i}`} className="p-4 bg-[#e3edf6] rounded border border-[#e2e8f0]">
                <div className="flex justify-between items-center mb-2 text-xs">
                  <span className="text-[#0f172a] font-semibold">{c.name}</span>
                  <span className="font-mono-x text-[#64748b]">
                    {c.email} • {new Date(c.created_at).toLocaleString("it-IT")}
                  </span>
                </div>
                <p className="text-xs text-[#475569] font-editorial leading-relaxed">{c.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setUser(null));
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // ignore
    }
    setUser(null);
  };

  return (
    <div className="bg-[#e3edf6] text-[#0f172a] min-h-screen">
      <nav className="border-b border-[#e2e8f0] px-6 md:px-8 py-5 bg-[#e3edf6]/90 backdrop-blur-md">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" data-testid="admin-nav-home" className="flex items-center gap-3">
            <img src="/logo-coppa.png" alt="Coppa dei Club" className="h-8 w-auto" />
            <span className="text-[#0284c7] font-mono-x text-xs">/ Admin</span>
          </Link>
          <div className="flex items-center gap-2 text-xs font-ui text-[#475569]">
            <a href="/#calendari" data-testid="admin-nav-calendari" className="hidden sm:inline-block px-4 py-2 bg-white text-[#0f172a] hover:bg-[#38bdf8] transition-all rounded font-semibold border border-[#cbd5e1]">
              Calendari Gare
            </a>
            <a href="/#regolamento" data-testid="admin-nav-regolamento" className="hidden sm:inline-block px-4 py-2 bg-white text-[#0f172a] hover:bg-[#38bdf8] transition-all rounded font-semibold border border-[#cbd5e1]">
              Regolamento
            </a>
            <Link to="/circoli" data-testid="admin-nav-circoli" className="hidden sm:inline-block px-4 py-2 bg-white text-[#0f172a] hover:bg-[#38bdf8] transition-all rounded font-semibold border border-[#cbd5e1]">
              Circoli
            </Link>
            <Link
              to="/admin"
              data-testid="admin-nav-login"
              className="hidden sm:inline-block px-4 py-2 bg-[#e2e8f0] text-[#0f172a] hover:bg-[#38bdf8] hover:text-[#0f172a] transition-all rounded font-semibold border border-[#cbd5e1]"
            >
              Login Admin
            </Link>
            <MobileNav />
          </div>
        </div>
      </nav>

      <main className="py-16 md:py-20 px-6 md:px-8">
        {user === undefined && (
          <p className="text-center text-[#475569] font-mono-x text-xs" data-testid="admin-loading">
            Verifica sessione...
          </p>
        )}
        {user === null && <LoginForm onSuccess={setUser} />}
        {user && <Dashboard user={user} onLogout={logout} />}
      </main>
    </div>
  );
}
